import { logger } from '@adornis/base/logging.js';
import {
  ChevronDown,
  Folder,
  FolderInput,
  Loader2,
  LogIn,
  Menu,
  MessageSquarePlus,
  MoreVertical,
  PanelLeftClose,
  Pencil,
  Plus,
  School,
  Settings,
  Trash2,
  User,
  UserPlus,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteExamMutation } from '../../../db/api/delete-exam.js';
import { getUnreadExamIdsQuery, markConversationAsReadMutation } from '../../../db/api/get-conversations.js';
import { getAllExamsQuery } from '../../../db/api/get-exams.js';
import { updateExamDataMutation, updateExamGroupMutation } from '../../../db/api/update-exam.js';
import type { Exam } from '../../../db/exam.js';
import { ALL_SUBJECTS, isSubject } from '../../../db/subjects.js';
import type { ViewState } from '../../app/types.js';
import {
  EXAM_NOTIFICATION_EVENT,
  EXAM_UPDATE_EVENT,
  examEvents,
  type ExamNotificationDetail,
} from '../../lib/events.js';
import { useCurrentUser } from '../../lib/useCurrentUser.js';
import { useCurrentUserProfile } from '../../lib/useCurrentUserProfile.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Logo } from '../ui/gradias-logo.js';
import { Input } from '../ui/input.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';

interface SidebarProps {
  onNavigate?: (view: ViewState, title?: string) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onOpenLogin?: () => void;
  onOpenSignup?: () => void;
  activeConversationId?: string;
}

export function Sidebar({
  onNavigate,
  isMobileOpen = false,
  onMobileClose,
  isCollapsed = false,
  onCollapsedChange,
  onOpenLogin,
  onOpenSignup,
  activeConversationId,
}: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const isGuest = !user;
  const { role } = useCurrentUserProfile();

  // Number of exams loaded per page: the initial load fetches this many, and each
  // "Ältere Klausuren laden" click fetches this many more.
  const EXAMS_PAGE_SIZE = 10;

  const [isCreating, setIsCreating] = useState(false);
  const [exams, setExams] = useState<Exam[]>([]);
  const [isLoadingExams, setIsLoadingExams] = useState(false);
  const [isLoadingMoreExams, setIsLoadingMoreExams] = useState(false);
  const [hasMoreExams, setHasMoreExams] = useState(false);
  const examListRef = useRef<HTMLDivElement>(null);
  // Mirror of the number of currently loaded exams, readable from stale closures
  // (e.g. the EXAM_UPDATE_EVENT listener) so a refresh preserves the loaded count.
  const loadedCountRef = useRef(0);
  // Selection set shared by the initial load and pagination requests.
  const examFields = {
    _id: 1,
    title: 1,
    subject: 1,
    topic: 1,
    class: 1,
    group: 1,
    conversationId: 1,
    createdAt: 1,
  } as const;

  useEffect(() => {
    loadedCountRef.current = exams.length;
  }, [exams]);

  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [unseenExamIds, setUnseenExamIds] = useState<Set<string>>(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'rename' | 'group' | null>(null);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [inputValue, setInputValue] = useState({ name: '', topic: '', subject: '', class: '' });
  const [selectedGroup, setSelectedGroup] = useState('');
  const [titleAutoSyncEnabled, setTitleAutoSyncEnabled] = useState(false);

  const normalizeTitle = (value?: string | null) =>
    String(value || '')
      .trim()
      .toLowerCase();

  // Map a stored subject to its localized display label. Canonical keys (e.g. "math") are localized;
  // legacy free-text values pass through unchanged.
  const localizeSubject = (subjectValue?: string | null): string => {
    const v = String(subjectValue || '').trim();
    if (!v) return '';
    return isSubject(v) ? t(`Subjects.${v}`) : v;
  };

  // Coerce a stored subject to a canonical key for the dropdown: a key is used as-is; a legacy
  // free-text value is matched against the localized labels; otherwise no selection.
  const coerceSubjectKey = (subjectValue?: string | null): string => {
    const v = String(subjectValue || '').trim();
    if (!v) return '';
    if (isSubject(v)) return v;
    const match = ALL_SUBJECTS.find(s => t(`Subjects.${s}`).toLowerCase() === v.toLowerCase());
    return match ?? '';
  };

  const buildAutoTitle = (classValue?: string | null, subjectValue?: string | null, topicValue?: string | null) => {
    const parts = [classValue, localizeSubject(subjectValue), topicValue]
      .map(part => String(part || '').trim())
      .filter(part => part.length > 0);
    return parts.join(' - ');
  };

  const loadExams = async () => {
    if (isGuest) {
      setExams([]);
      setHasMoreExams(false);
      setIsLoadingExams(false);
      return;
    }

    setIsLoadingExams(true);
    try {
      // Load the first page; on refresh, re-fetch as many exams as are currently shown so
      // an update/rename/delete doesn't collapse the loaded list back to a single page.
      const limit = Math.max(EXAMS_PAGE_SIZE, loadedCountRef.current);
      // Fetch one extra as a "scout" to know whether more exist, then drop it from display.
      const result = await getAllExamsQuery(limit + 1, 0)(examFields);
      const hasMore = result.length > limit;
      setExams(hasMore ? result.slice(0, limit) : result);
      setHasMoreExams(hasMore);
    } catch (err) {
      logger.error(err);
    } finally {
      setIsLoadingExams(false);
    }
  };

  const loadMoreExams = async () => {
    if (isGuest || isLoadingExams || isLoadingMoreExams || !hasMoreExams) return;

    setIsLoadingMoreExams(true);
    try {
      // Fetch one extra as a "scout" to know whether more exist, then drop it from display.
      const result = await getAllExamsQuery(EXAMS_PAGE_SIZE + 1, loadedCountRef.current)(examFields);
      const hasMore = result.length > EXAMS_PAGE_SIZE;
      const page = hasMore ? result.slice(0, EXAMS_PAGE_SIZE) : result;
      setExams(prev => {
        // Guard against duplicates in case the list changed between requests.
        const seen = new Set(prev.map(exam => exam._id));
        const next = page.filter(exam => !seen.has(exam._id));
        return [...prev, ...next];
      });
      setHasMoreExams(hasMore);
    } catch (err) {
      logger.error(err);
    } finally {
      setIsLoadingMoreExams(false);
    }
  };

  useEffect(() => {
    if (!user?._id) {
      setUnseenExamIds(new Set());
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const ids = await getUnreadExamIdsQuery()();
        if (cancelled) return;
        setUnseenExamIds(new Set(Array.isArray(ids) ? ids.filter((id): id is string => Boolean(id)) : []));
      } catch (err) {
        logger.warn(err, `Sidebar: failed to load unread exam IDs`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  useEffect(() => {
    if (!activeConversationId || activeConversationId === 'new') return;
    const matchedExam = exams.find(exam => exam.conversationId === activeConversationId);
    if (!matchedExam) return;
    if (!unseenExamIds.has(matchedExam._id)) return;
    setUnseenExamIds(prev => {
      const next = new Set(prev);
      next.delete(matchedExam._id);
      return next;
    });
    void markConversationAsReadMutation(activeConversationId)().catch(err =>
      logger.warn(err, `Sidebar: failed to mark conversation as read on active change`),
    );
  }, [activeConversationId, exams, unseenExamIds]);

  useEffect(() => {
    loadExams();
    const handleUpdate = () => {
      loadExams();
    };
    const handleNotification = (event: Event) => {
      const detail = (event as CustomEvent<ExamNotificationDetail>).detail;
      if (!detail?.examId) return;
      setUnseenExamIds(prev => {
        if (prev.has(detail.examId)) return prev;
        const next = new Set(prev);
        next.add(detail.examId);
        return next;
      });
    };
    examEvents.addEventListener(EXAM_UPDATE_EVENT, handleUpdate);
    examEvents.addEventListener(EXAM_NOTIFICATION_EVENT, handleNotification);
    return () => {
      examEvents.removeEventListener(EXAM_UPDATE_EVENT, handleUpdate);
      examEvents.removeEventListener(EXAM_NOTIFICATION_EVENT, handleNotification);
    };
  }, [user]);

  const openDialog = (type: 'rename' | 'group', exam: Exam) => {
    setSelectedExam(exam);
    setDialogType(type);

    if (type === 'rename') {
      const subject = coerceSubjectKey(exam.subject);
      const topic = exam.topic || '';
      const classValue = exam.class || '';
      const autoTitle = buildAutoTitle(classValue, subject, topic);
      const hasAutoTitle = normalizeTitle(exam.title) === normalizeTitle(autoTitle) && autoTitle.length > 0;

      setTitleAutoSyncEnabled(hasAutoTitle);
      setInputValue({
        name: exam.title || autoTitle,
        subject,
        topic,
        class: classValue,
      });
    } else if (type === 'group') {
      setSelectedGroup(exam.group || '');
    }

    setDialogOpen(true);
    setActiveMenuId(null);
  };

  const handleDialogSubmit = async () => {
    if (!selectedExam) return;

    try {
      if (dialogType === 'rename') {
        const previousAutoTitle = buildAutoTitle(selectedExam.class, selectedExam.subject, selectedExam.topic);
        const wasAutoTitle =
          normalizeTitle(selectedExam.title) === normalizeTitle(previousAutoTitle) && previousAutoTitle.length > 0;

        const nextSubject = inputValue.subject.trim();
        const nextTopic = inputValue.topic.trim();
        const nextClass = inputValue.class.trim();
        const nextAutoTitle = buildAutoTitle(nextClass, nextSubject, nextTopic);

        const typedTitle = inputValue.name.trim();
        const shouldKeepAutoSync = wasAutoTitle && titleAutoSyncEnabled;
        const finalTitle = shouldKeepAutoSync
          ? nextAutoTitle || typedTitle
          : typedTitle || selectedExam.title || nextAutoTitle;

        if (finalTitle.trim() !== '') {
          await updateExamDataMutation(selectedExam._id, finalTitle, nextSubject, nextTopic, nextClass)();
          await loadExams();
        }
      } else if (dialogType === 'group') {
        if (selectedGroup !== selectedExam.group) {
          await updateExamGroupMutation(selectedExam._id, selectedGroup)();
          await loadExams();
        }
      }
      setDialogOpen(false);
    } catch (error) {
      logger.error(error);
      alert(t('Sidebar.alerts.saveFailed'));
    }
  };

  const handleDelete = async (examId: string) => {
    setActiveMenuId(null);
    if (window.confirm(t('Sidebar.alerts.deleteConfirm'))) {
      try {
        await deleteExamMutation(examId)();
        await loadExams();
      } catch (error) {
        logger.error(error);
      }
    }
  };

  const handleNavClick = (view: ViewState, title: string) => {
    if (onNavigate) {
      onNavigate(view, title);
    }
    if (onMobileClose) {
      onMobileClose();
    }
  };

  const handleExamClick = (exam: Exam) => {
    if (onNavigate && exam.conversationId) {
      onNavigate('chat', exam.conversationId);
    }
    if (unseenExamIds.has(exam._id)) {
      setUnseenExamIds(prev => {
        const next = new Set(prev);
        next.delete(exam._id);
        return next;
      });
      if (exam.conversationId) {
        void markConversationAsReadMutation(exam.conversationId)().catch(err =>
          logger.warn(err, `Sidebar: failed to mark conversation as read on click`),
        );
      }
    }
    if (onMobileClose) {
      onMobileClose();
    }
  };

  const handleCreateNewExam = () => {
    if (onNavigate) {
      onNavigate('chat', 'new');
    }
    if (onMobileClose) onMobileClose();
  };

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 bg-transparent z-40 md:hidden" onClick={onMobileClose} aria-hidden />
      )}

      {activeMenuId && (
        <div
          className="fixed inset-0 z-[50]"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setActiveMenuId(null);
            }
          }}
        />
      )}

      <aside
        className={cn(
          'flex flex-col h-screen font-medium transition-all duration-300 ease-in-out shadow-lg backdrop-blur-md',
          'bg-sidebar text-sidebar-foreground border-r border-sidebar-border',
          'md:relative md:translate-x-0',
          isCollapsed ? 'md:w-[80px]' : 'md:w-[260px]',
          'fixed top-0 left-0 z-50 w-[280px]',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        <div className={cn('p-6 shrink-0 flex items-center', isCollapsed ? 'justify-center' : 'justify-between')}>
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <Logo
                onClick={() => {
                  onNavigate?.('chat');
                  onMobileClose?.();
                }}
              />
              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-sidebar-primary text-sidebar-primary-foreground rounded select-none">
                {t('Sidebar.badges.beta')}
              </span>
            </div>
          )}

          <button
            onClick={() => onCollapsedChange?.(!isCollapsed)}
            className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground p-2 rounded-md transition-colors hidden md:block"
          >
            {isCollapsed ? <Menu className="h-6 w-6" /> : <PanelLeftClose className="h-5 w-5 opacity-80" />}
          </button>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden px-4 pb-4">
          {isGuest ? (
            <div
              className={cn('flex flex-col flex-1', isCollapsed ? 'items-center pt-0' : 'items-center justify-center')}
            >
              {!isCollapsed ? (
                <>
                  <div className="text-center mb-6">
                    <h3 className="font-bold text-lg mb-2">{t('Sidebar.guest.title')}</h3>
                    <p className="text-sidebar-foreground/80 text-sm leading-relaxed">
                      {t('Sidebar.guest.description')}
                    </p>
                  </div>

                  <button
                    onClick={onOpenLogin}
                    disabled={!onOpenLogin}
                    className="flex items-center justify-center gap-3 transition-all shadow-sm font-semibold tracking-wide bg-sidebar-primary text-sidebar-primary-foreground hover:opacity-90 w-full py-3 px-4 rounded-lg"
                    title={t('Sidebar.guest.loginButton')}
                  >
                    <LogIn className="h-5 w-5 shrink-0" />
                    <span>{t('Sidebar.guest.loginButton')}</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={onOpenLogin}
                  disabled={!onOpenLogin}
                  className="flex items-center justify-center w-full p-3 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl transition-colors"
                  title={t('Sidebar.guest.loginButton')}
                >
                  <LogIn className="h-6 w-6 shrink-0" />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className={cn('flex shrink-0 mb-6', isCollapsed ? 'justify-center' : '')}>
                <button
                  onClick={handleCreateNewExam}
                  disabled={isCreating}
                  className={cn(
                    'flex items-center gap-3 transition-all shadow-sm font-semibold tracking-wide',
                    'bg-sidebar-primary text-sidebar-primary-foreground hover:opacity-90',
                    isCollapsed ? 'p-3 rounded-xl' : 'w-full px-4 py-3 rounded-lg',
                    isCreating ? 'opacity-70 cursor-wait' : '',
                  )}
                  title={t('Sidebar.actions.newExamTitle')}
                >
                  {isCreating ? (
                    <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                  ) : (
                    <Plus className="h-5 w-5 shrink-0" />
                  )}

                  {!isCollapsed && (
                    <span>{isCreating ? t('Sidebar.actions.creating') : t('Sidebar.actions.newExam')}</span>
                  )}
                </button>
              </div>

              <div
                className={cn(
                  'flex flex-col flex-1 overflow-hidden transition-opacity',
                  isCollapsed ? 'items-center' : '',
                )}
              >
                {!isCollapsed ? (
                  <div className="shrink-0 flex items-center gap-2 mb-3 text-sm text-sidebar-foreground/70 font-semibold uppercase tracking-wider">
                    <School className="h-4 w-4" />
                    <span>{t('Sidebar.examList.title')}</span>
                  </div>
                ) : (
                  <div className="shrink-0 mb-4 border-b border-sidebar-border pb-2 w-full flex justify-center">
                    <School className="h-5 w-5 text-sidebar-foreground/70" />
                  </div>
                )}

                <div
                  ref={examListRef}
                  className={cn(
                    'flex-1 overflow-y-auto space-y-1 pr-1',
                    !isCollapsed && 'ml-2 border-l border-sidebar-border pl-3',
                  )}
                >
                  {isLoadingExams && (
                    <div className="flex items-center justify-center py-4 opacity-70">
                      <Loader2 className="h-4 w-4 animate-spin text-sidebar-foreground" />
                    </div>
                  )}

                  {!isLoadingExams && exams.length === 0 && !isCollapsed && (
                    <div className="text-sm text-sidebar-foreground/60 italic py-2">
                      {t('Sidebar.examList.noExams')}
                    </div>
                  )}

                  {exams.map(exam => (
                    <div
                      key={exam._id}
                      onClick={() => handleExamClick(exam)}
                      className="relative flex items-center gap-3 text-sm group cursor-pointer text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent p-2 rounded-md transition-colors"
                      title={
                        exam.subject && exam.group
                          ? `${exam.subject} - ${exam.group}`
                          : exam.title || t('Sidebar.examList.unnamed')
                      }
                    >
                      <span className="relative shrink-0">
                        <Folder className="h-5 w-5" />
                        {unseenExamIds.has(exam._id) && (
                          <span
                            className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-brand-strong"
                            aria-label="Unread update"
                          />
                        )}
                      </span>

                      {!isCollapsed && (
                        <>
                          <span className="truncate">
                            {exam.subject && exam.group
                              ? `${exam.subject} - ${exam.group}`
                              : exam.title || t('Sidebar.examList.unnamedExam')}
                          </span>

                          <div
                            className={cn(
                              'ml-auto p-1 transition-colors duration-200 rounded-md',
                              activeMenuId === exam._id
                                ? 'opacity-100 text-sidebar-foreground bg-sidebar-accent'
                                : 'opacity-0 group-hover:opacity-100 text-sidebar-foreground/50 hover:text-sidebar-foreground',
                              'z-[70]',
                            )}
                            onClick={e => {
                              e.stopPropagation();
                              setActiveMenuId(activeMenuId === exam._id ? null : exam._id);
                            }}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </div>

                          {/* Das Dialog Menü im Dropdown - EXAKT Semantic */}
                          {activeMenuId === exam._id && (
                            <div
                              className="absolute right-0 top-9 w-48 bg-popover text-popover-foreground rounded-lg shadow-xl border border-border z-[75] overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-right"
                              onClick={e => {
                                e.stopPropagation();
                              }}
                            >
                              <div className="flex flex-col py-1">
                                <button
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openDialog('rename', exam);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 hover:bg-accent hover:text-accent-foreground text-sm w-full text-left transition-colors"
                                >
                                  <Pencil className="h-4 w-4 text-muted-foreground" />
                                  <span>{t('Sidebar.examMenu.edit')}</span>
                                </button>

                                <button
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openDialog('group', exam);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 hover:bg-accent hover:text-accent-foreground text-sm w-full text-left transition-colors"
                                >
                                  <FolderInput className="h-4 w-4 text-muted-foreground" />
                                  <span>{t('Sidebar.examMenu.addToClass')}</span>
                                </button>

                                <div className="h-px bg-border/50 my-1" />

                                <button
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDelete(exam._id);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 hover:bg-destructive/10 text-destructive text-sm w-full text-left transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span>{t('Sidebar.examMenu.delete')}</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {!isCollapsed && hasMoreExams && (
                    <button
                      type="button"
                      onClick={() => void loadMoreExams()}
                      disabled={isLoadingMoreExams || isLoadingExams}
                      className="flex w-full flex-col items-center justify-center gap-1 py-2.5 text-xs font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors disabled:cursor-wait"
                    >
                      <span>{t('Sidebar.examList.loadOlder')}</span>
                      {isLoadingMoreExams ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  )}

                  {isCollapsed && isLoadingMoreExams && (
                    <div className="flex items-center justify-center py-3 opacity-70">
                      <Loader2 className="h-4 w-4 animate-spin text-sidebar-foreground" />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-4 mt-auto border-t border-sidebar-border space-y-1 shrink-0">
          {!isGuest && (
            <button
              onClick={() => handleNavClick('profile', t('Sidebar.bottomNav.profile'))}
              className={cn(
                'flex items-center gap-3 text-sm text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors font-medium',
                isCollapsed ? 'justify-center p-2' : 'w-full px-2 py-2.5',
              )}
              title={t('Sidebar.bottomNav.profile')}
            >
              <User className="h-5 w-5 shrink-0" />
              {!isCollapsed && (
                <span className="flex items-center gap-2">
                  {t('Sidebar.bottomNav.profile')}
                  {role && (
                    <Badge className="bg-sidebar-primary text-sidebar-primary-foreground">
                      {role === 'teacher' ? t('Profile.personal.role.teacher') : t('Profile.personal.role.student')}
                    </Badge>
                  )}
                </span>
              )}
            </button>
          )}

          {!isGuest && role === 'teacher' && (
            <button
              onClick={() => handleNavClick('classroom', t('Sidebar.bottomNav.classroom'))}
              className={cn(
                'flex items-center gap-3 text-sm text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors font-medium',
                isCollapsed ? 'justify-center p-2' : 'w-full px-2 py-2.5',
              )}
              title={t('Sidebar.bottomNav.classroom')}
            >
              <Users className="h-5 w-5 shrink-0" />
              {!isCollapsed && <span>{t('Sidebar.bottomNav.classroom')}</span>}
            </button>
          )}

          {!isGuest && role === 'student' && (
            <button
              onClick={() => handleNavClick('joinClass', t('Sidebar.bottomNav.joinClass'))}
              className={cn(
                'flex items-center gap-3 text-sm text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors font-medium',
                isCollapsed ? 'justify-center p-2' : 'w-full px-2 py-2.5',
              )}
              title={t('Sidebar.bottomNav.joinClass')}
            >
              <UserPlus className="h-5 w-5 shrink-0" />
              {!isCollapsed && <span>{t('Sidebar.bottomNav.joinClass')}</span>}
            </button>
          )}

          <button
            onClick={() => handleNavClick('settings', t('Sidebar.bottomNav.settings'))}
            className={cn(
              'flex items-center gap-3 text-sm text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors font-medium',
              isCollapsed ? 'justify-center p-2' : 'w-full px-2 py-2.5',
            )}
            title={t('Sidebar.bottomNav.settings')}
          >
            <Settings className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span>{t('Sidebar.bottomNav.settings')}</span>}
          </button>

          <button
            onClick={() => handleNavClick('feedback', t('Sidebar.bottomNav.feedback'))}
            className={cn(
              'flex items-center gap-3 text-sm text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors font-medium',
              isCollapsed ? 'justify-center p-2' : 'w-full px-2 py-2.5',
            )}
            title={t('Sidebar.bottomNav.feedback')}
          >
            <MessageSquarePlus className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span>{t('Sidebar.bottomNav.feedback')}</span>}
          </button>
        </div>
      </aside>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* EXAKT Semantic für den Dialog! */}
        <DialogContent className="sm:max-w-[425px] bg-card text-card-foreground border-border">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'rename' ? t('Sidebar.dialogs.renameTitle') : t('Sidebar.dialogs.groupTitle')}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {dialogType === 'rename' ? t('Sidebar.dialogs.renameDesc') : t('Sidebar.dialogs.groupDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {dialogType === 'rename' ? (
              <div className="grid grid-cols-4 items-center gap-4">
                <span className="text-right text-sm font-medium">{t('Sidebar.dialogs.fields.name')}</span>
                <Input
                  id="name"
                  value={inputValue.name}
                  onChange={e => {
                    setTitleAutoSyncEnabled(false);
                    setInputValue({ ...inputValue, name: e.target.value });
                  }}
                  className="col-span-3 bg-background text-foreground border-input focus-visible:ring-brand-primary"
                  autoFocus
                />
                <span className="text-right text-sm font-medium">{t('Sidebar.dialogs.fields.subject')}</span>
                <Select
                  value={inputValue.subject || undefined}
                  onValueChange={nextSubject => {
                    setInputValue(prev => {
                      const next = { ...prev, subject: nextSubject };
                      if (titleAutoSyncEnabled) {
                        next.name = buildAutoTitle(next.class, next.subject, next.topic);
                      }
                      return next;
                    });
                  }}
                >
                  <SelectTrigger
                    id="subject"
                    className="col-span-3 bg-background text-foreground border-input focus-visible:ring-brand-primary"
                  >
                    <SelectValue placeholder={t('Sidebar.dialogs.fields.subject')} />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_SUBJECTS.map(subjectKey => (
                      <SelectItem key={subjectKey} value={subjectKey}>
                        {t(`Subjects.${subjectKey}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-right text-sm font-medium">{t('Sidebar.dialogs.fields.topic')}</span>
                <Input
                  id="topic"
                  value={inputValue.topic}
                  onChange={e => {
                    const nextTopic = e.target.value;
                    setInputValue(prev => {
                      const next = { ...prev, topic: nextTopic };
                      if (titleAutoSyncEnabled) {
                        next.name = buildAutoTitle(next.class, next.subject, next.topic);
                      }
                      return next;
                    });
                  }}
                  className="col-span-3 bg-background text-foreground border-input focus-visible:ring-brand-primary"
                  placeholder={t('Sidebar.dialogs.fields.topic')}
                />
                <span className="text-right text-sm font-medium">{t('Sidebar.dialogs.fields.class')}</span>
                <Input
                  id="class"
                  value={inputValue.class}
                  onChange={e => {
                    const nextClass = e.target.value;
                    setInputValue(prev => {
                      const next = { ...prev, class: nextClass };
                      if (titleAutoSyncEnabled) {
                        next.name = buildAutoTitle(next.class, next.subject, next.topic);
                      }
                      return next;
                    });
                  }}
                  className="col-span-3 bg-background text-foreground border-input focus-visible:ring-brand-primary"
                  placeholder={t('Sidebar.dialogs.fields.class')}
                />
              </div>
            ) : (
              <div className="grid grid-cols-4 items-center gap-4">
                <span className="text-right text-sm font-medium">{t('Sidebar.dialogs.fields.class')}</span>

                <div className="col-span-3">
                  <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                    <SelectTrigger className="w-full bg-background text-foreground border-input focus:ring-brand-primary">
                      <SelectValue placeholder={t('Sidebar.dialogs.fields.placeholderSelect')} />
                    </SelectTrigger>
                    <SelectContent className="bg-popover text-popover-foreground border-border">
                      <SelectItem value="Klasse 10a">Klasse 10a</SelectItem>
                      <SelectItem value="Klasse 10b">Klasse 10b</SelectItem>
                      <SelectItem value="Kurs Mathe GK">Kurs Mathe GK</SelectItem>
                      <SelectItem value="Informatik LK">Informatik LK</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="submit"
              onClick={handleDialogSubmit}
              className="bg-[var(--color-brand-300)] hover:bg-[var(--color-brand-300)] text-white"
            >
              {t('Sidebar.dialogs.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
